<!--@format-->
<template>
    <div class="account-company-individual-quota-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Edit Individual Quota</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading" class="loading-card">
                    <loading-screen-svg />
                </div>
                <div v-else class="form-wrapper">
                    <div class="title">
                        <app-profile-image
                            :src="salesperson.profileImageUrl | profileImagePlaceholder"
                        />
                        {{ salesperson.name }}'s Monthly Sales Quota
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.monthlyMrrQuota"
                            label="MRR Quota"
                            placeholder="$0.00"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.monthlyMrrQuota.required"
                            error="MRR Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.monthlyRevenueQuota"
                            label="Gross Revenue Quota"
                            placeholder="$0.00"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.monthlyRevenueQuota.required"
                            error="Gross Revenue Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.monthlyMarginQuota"
                            label="Gross Margin Quota"
                            placeholder="$0.00"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.monthlyMarginQuota.required"
                            error="Gross Margin Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.callQuota"
                            label="Call Quota"
                            placeholder="0"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.callQuota.required"
                            error="Call Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.emailQuota"
                            label="Email Quota"
                            placeholder="0"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.emailQuota.required"
                            error="Email Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.meetingQuota"
                            label="Meeting Quota"
                            placeholder="0"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.meetingQuota.required"
                            error="Meeting Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.opportunityQuota"
                            label="Opportunity Quota"
                            placeholder="0"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.opportunityQuota.required"
                            error="Opportunity Quota is required"
                        />
                    </div>
                    <div class="input-container">
                        <app-input
                            v-model="salesperson.winQuota"
                            label="Win Quota"
                            placeholder="0"
                            type="number"
                            :is-valid="!submitted || $v.salesperson.winQuota.required"
                            error="Win Quota is required"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';

export default {
    name: 'account-company-individual-quota-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-profile-image': AppProfileImage,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            userId: this.widget.userId,
            submitted: false,
            salesperson: {
                monthlyMrrQuota: 0,
                monthlyRevenueQuota: 0,
                monthlyMarginQuota: 0,
                callQuota: 0,
                emailQuota: 0,
                meetingQuota: 0,
                opportunityQuota: 0,
                winQuota: 0,
                name: 'Salesperson',
                profileImageUrl: null,
            },
        };
    },

    validations: {
        salesperson: {
            monthlyMrrQuota: { required },
            monthlyRevenueQuota: { required },
            monthlyMarginQuota: { required },
            callQuota: { required },
            emailQuota: { required },
            meetingQuota: { required },
            opportunityQuota: { required },
            winQuota: { required },
        },
    },

    created() {
        this.getQuota();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.patch();
        },

        patch() {
            const changes = [
                {
                    name: 'MonthlyMrrQuota',
                    value: this.salesperson.monthlyMrrQuota,
                },
                {
                    name: 'MonthlyRevenueQuota',
                    value: this.salesperson.monthlyRevenueQuota,
                },
                {
                    name: 'MonthlyMarginQuota',
                    value: this.salesperson.monthlyMarginQuota,
                },
                {
                    name: 'CallQuota',
                    value: this.salesperson.callQuota,
                },
                {
                    name: 'EmailQuota',
                    value: this.salesperson.emailQuota,
                },
                {
                    name: 'MeetingQuota',
                    value: this.salesperson.meetingQuota,
                },
                {
                    name: 'OpportunityQuota',
                    value: this.salesperson.opportunityQuota,
                },
                {
                    name: 'WinQuota',
                    value: this.salesperson.winQuota,
                },
            ];

            this.$api
                .patch(`/api/Users/${this.userId}`, changes)
                .then(() => {
                    EventBus.$emit('account-company-individual-quota:update');
                    EventBus.$emit('close-widget', this.widget);
                    this.$helpers.showSuccessNotification(
                        'Quota has been successfully updated for ' + this.salesperson.name + '.'
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your salesperson monthly sales quotas.',
                        { error },
                    );
                });
        },

        getQuota() {
            this.isLoading = true;
            const select = '$select=ProfileImageUrl,FirstName,LastName,MonthlyRevenueQuota,MonthlyMarginQuota,MonthlyMrrQuota,CallQuota,EmailQuota,MeetingQuota,OpportunityQuota,WinQuota';
            const filter = `$filter=Id eq '${this.userId}'`;
            const query = `/service/Users?${filter}&${select}`;

            this.$api
                .get(query)
                .then((response) => {
                    const responseData = response.data.value[0];

                    this.salesperson.monthlyRevenueQuota = responseData.MonthlyRevenueQuota;
                    this.salesperson.monthlyMarginQuota = responseData.MonthlyMarginQuota;
                    this.salesperson.monthlyMrrQuota = responseData.MonthlyMrrQuota;
                    this.salesperson.callQuota = responseData.CallQuota;
                    this.salesperson.emailQuota = responseData.EmailQuota;
                    this.salesperson.meetingQuota = responseData.MeetingQuota;
                    this.salesperson.opportunityQuota = responseData.OpportunityQuota;
                    this.salesperson.winQuota = responseData.WinQuota;
                    this.salesperson.profileImageUrl = responseData.ProfileImageUrl;
                    this.salesperson.name = this.$helpers.getName(
                        responseData.FirstName,
                        responseData.LastName
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your information.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.account-company-individual-quota-widget .form-group {
    margin-top: 1em;
}

.account-company-individual-quota-widget .app-input {
    margin-bottom: 1em;
}

.account-company-individual-quota-widget .title {
    display: flex;
    align-items: center;
    font-weight: bold;
    margin-bottom: 24px;
}

.account-company-individual-quota-widget .app-profile-image {
    margin-right: 10px;
}

.account-company-individual-quota-widget .content-wrapper .loading-card {
    text-align: center;
}

.account-company-individual-quota-widget .form-wrapper > .input-container {
    margin-bottom: 1.5em;
}

.account-company-individual-quota-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.account-company-individual-quota-widget .cancel-btn:hover {
    background: #e9e9ef;
}
</style>
